import type {
  PaymentsStatusDto,
  DonationHealthDto,
  DonationFeedItemDto,
  DisputeItemDto,
  StripeConnectPanelState,
  StripePayoutDto,
  StripePayoutStatus,
} from '@signalvote/shared';
import {
  stripeConnectPanelCopy,
  deriveNextPayout,
  isStripePayoutStatus,
  payoutScheduleCopy,
  arePayoutsPaused,
} from '@signalvote/shared';
import { Link } from '@tanstack/react-router';
import { useRefresh } from '@signalvote/data';
import { AlertTriangle, ShieldAlert, Info, Ban } from 'lucide-react';
import {
  useDonationSummary,
  donationSummaryRead,
  donationsFeedRead,
  needsAttentionRead,
  useDonationsFeed,
  useNeedsAttention,
  useStripeBalance,
  useStripePayoutSchedule,
  useStripePayouts,
} from '@/hooks/queries/useStripeConnectPayments';
import { formatAmount, fmtDateLong, fmtDateShort } from '@/components/finance/utils';
import { DonationStatusBadge } from './donationStatus';
import { DonationFeedSheet } from './DonationFeed';
import { RecurringDonors } from './RecurringDonors';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '@/components/ui/card';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from '@/components/ui/tooltip';

// The wire carries integer cents; format at the render boundary via the shared
// finance currency formatter (dollars in).
function formatCents(cents: number): string {
  return formatAmount(cents / 100);
}

interface DonationDashboardProps {
  status: PaymentsStatusDto;
  /** Re-shows the embedded verification flow inline (owned by the parent page). */
  onFinishVerification: () => void;
  /**
   * Demo campaigns accept simulated gifts with no Stripe connected account, so the live
   * balance/payout/health panels can't load. In demo mode we render only the DB-sourced
   * donations summary + recent-donations feed (which DO reflect the simulated gifts).
   */
  demo?: boolean;
}

/**
 * The post-onboarding donation dashboard shell. Paints instantly from our DB
 * (health + donations/fees this period); the live-only balance/payouts panels and
 * the donation feed arrive in later slices. Deliberately never headlines a bare
 * "Raised" — total-raised-across-every-source and cash-on-hand stay on the finance
 * dashboard (ADR-017); this is the operational money-health view.
 */
export function DonationDashboard({ status, onFinishVerification, demo = false }: DonationDashboardProps) {
  const summaryRead = useDonationSummary();
  const summary = summaryRead.status === 'ready' ? summaryRead.data : undefined;
  const isLoading = summaryRead.status === 'loading' || summaryRead.status === 'idle';
  const refreshRead = useRefresh();

  // A "payouts-off" account (past due / restricted / payouts disabled) is the
  // loud, money-critical case. The HealthBanner and the two live money cards read
  // the SAME shared derivation so they tell one consistent story — a paused
  // account, not a scatter of unrelated glitches. When payouts are stopped, the
  // live Stripe cards echo the banner's pause instead of degrading to a generic
  // "couldn't load" retry (which collides with, and hides, the real cause).
  const payoutsOff = summary ? arePayoutsPaused(summary.health) : false;

  // Name the "this period" window: the current calendar month in campaign
  // timezone (server-computed `periodStart`, a yyyy-MM-dd calendar date). Format
  // with the DST-safe `fmtDateShort` (parses at local noon) so the day-of-month
  // never rolls back across a browser timezone — never a naked `new Date()`.
  const periodLabel = summary
    ? `This month — since ${fmtDateShort(summary.periodStart)}`
    : null;

  return (
    <section className="space-y-6" data-testid="donation-dashboard">
      {/* The health banner + live-Stripe cards all need a connected account; a demo campaign has
          none, so they're hidden and only the DB-sourced donations summary shows. */}
      {!demo && (
        <HealthBanner
          health={summary?.health}
          loading={isLoading}
          onFinishVerification={onFinishVerification}
        />
      )}

      <div
        className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4"
        data-testid="donation-hero"
      >
        {/* The two live-Stripe cards load async on their own hooks — they never block
            the DB donation card or the health banner above. */}
        {!demo && <AvailableToPayOut payoutsOff={payoutsOff} />}
        {!demo && <NextPayoutCard payoutsOff={payoutsOff} />}
        <HeroCard
          title="Donations this period"
          subtitle={
            <>
              {periodLabel && (
                <span className="block" data-testid="donations-period">
                  {periodLabel}
                </span>
              )}
              <span className="block">through your donation page</span>
            </>
          }
          loading={isLoading}
          error={summaryRead.status === 'failed' || summaryRead.status === 'offline'}
          onRetry={() => void refreshRead(donationSummaryRead, undefined)}
          testId="donations-card"
        >
          {summary && (
            <>
              <p className="text-2xl font-bold text-foreground" data-testid="donations-amount">
                {formatCents(summary.donationsThisPeriodCents)}
              </p>
              <p className="text-sm text-muted-foreground">
                {summary.donationsThisPeriodCount === 1
                  ? '1 gift'
                  : `${summary.donationsThisPeriodCount} gifts`}
              </p>
              <div
                className="mt-3 flex items-center justify-between gap-3 border-t pt-3"
                data-testid="fees-secondary"
              >
                <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
                  Processing fees deducted
                  <FeeInfoTooltip />
                </span>
                <span className="text-sm font-semibold text-foreground" data-testid="fees-amount">
                  {formatCents(summary.processingFeesDeductedThisPeriodCents)}
                </span>
              </div>
            </>
          )}
        </HeroCard>
      </div>

      <p className="text-xs text-muted-foreground">
        Total raised across every source and cash on hand live on your{' '}
        <Link to="/admin/finance" className="font-medium underline underline-offset-2">
          Finance dashboard
        </Link>
        .
      </p>

      {!demo && summary && (
        <FirstPayoutNotice
          hasPortalDonation={summary.hasAnyDonations}
          payoutsOff={payoutsOff}
        />
      )}

      {!demo && summary && <NeedsAttention />}

      {summary &&
        (summary.hasAnyDonations ? (
          <>
            <RecentDonations />
            {/* Recurring subscriptions need a real connected account, so not in demo. */}
            {!demo && <RecurringDonors />}
            {!demo && <PayoutsPanel />}
          </>
        ) : (
          <EmptyDonations status={status} />
        ))}
    </section>
  );
}

// ---------------------------------------------------------------------------
// First-payout notice — shown only while a campaign's first portal funds are
// visibly pending and Stripe has never created a payout. Live read failures stay
// silent here; their owning cards provide Retry affordances.
// ---------------------------------------------------------------------------

function FirstPayoutNotice({
  hasPortalDonation,
  payoutsOff,
}: {
  hasPortalDonation: boolean;
  payoutsOff: boolean;
}) {
  const { data: balance } = useStripeBalance();
  const { data: payoutPages } = useStripePayouts();
  const hasStripePayout =
    payoutPages?.pages.some((page) => page.payouts.length > 0) ?? false;

  if (
    !hasPortalDonation ||
    payoutsOff ||
    !balance ||
    balance.pendingCents <= 0 ||
    !payoutPages ||
    hasStripePayout
  ) {
    return null;
  }

  return (
    <div
      className="flex items-start gap-3 rounded-md border border-blue-300 bg-blue-50 px-4 py-3 text-blue-950"
      data-testid="first-payout-notice"
    >
      <Info className="mt-0.5 h-5 w-5 shrink-0 text-blue-700" aria-hidden />
      <div className="space-y-1">
        <p className="text-sm font-semibold">Your first payout is being prepared</p>
        <p className="text-sm text-blue-900">
          First payouts can take 7–14 days. After that, card payments typically
          become available within 3 business days.
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Needs-attention card — quietly surfaces open disputes/chargebacks, read-only.
// Hides entirely when there is nothing to surface. "We handle disputes" copy so
// the treasurer knows there's no action required beyond adding evidence via
// support.
// ---------------------------------------------------------------------------

function NeedsAttention() {
  const read = useNeedsAttention();
  const refreshRead = useRefresh();
  if (read.status === 'failed' || read.status === 'offline') {
    return (
      <p className="text-sm text-muted-foreground" data-testid="needs-attention-unready">
        Couldn&apos;t check for disputed donations.{' '}
        <button
          type="button"
          onClick={() => void refreshRead(needsAttentionRead, undefined)}
          className="font-medium text-primary underline underline-offset-2"
        >
          Retry
        </button>
      </p>
    );
  }
  const disputes = read.status === 'ready' ? read.data.disputes : [];

  if (disputes.length === 0) {
    return null;
  }

  return (
    <Card
      className="border-amber-300 bg-amber-50/60"
      data-testid="needs-attention"
    >
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold text-amber-900">
          <ShieldAlert className="h-4 w-4 shrink-0" aria-hidden />
          {disputes.length === 1 ? '1 donation disputed' : `${disputes.length} donations disputed`}
        </CardTitle>
        <CardDescription className="text-amber-900/80">
          We handle disputes for you — no action needed. Contact support if you
          want to add evidence.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-1">
        {disputes.map((dispute) => (
          <DisputeRow key={dispute.id} dispute={dispute} />
        ))}
      </CardContent>
    </Card>
  );
}

function DisputeRow({ dispute }: { dispute: DisputeItemDto }) {
  return (
    <div
      className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-1 text-sm"
      data-testid="dispute-row"
    >
      <span className="font-medium text-foreground">{dispute.donorName}</span>
      <span className="flex items-baseline gap-2 text-muted-foreground">
        <span className="font-medium text-foreground">
          {formatCents(dispute.amountCents)}
        </span>
        <span>{fmtDateLong(dispute.disputedAt)}</span>
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Recent donations — the first page of portal donations, newest first, with a
// derived status pill. Its own loading / error-retry states, independent of the
// summary. The filterable "View all" feed is a later slice.
// ---------------------------------------------------------------------------

function RecentDonations() {
  const read = useDonationsFeed();
  const refreshRead = useRefresh();
  const items = read.status === 'ready' ? read.data.items : [];

  return (
    <Card data-testid="recent-donations">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Recent donations
        </CardTitle>
        {/* Opens the full filterable feed (status tabs + per-donation detail) as a slide-over. */}
        <DonationFeedSheet />
      </CardHeader>
      <CardContent>
        {read.status === 'loading' || read.status === 'idle' ? (
          <div className="space-y-2" data-testid="recent-donations-skeleton">
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
          </div>
        ) : read.status === 'failed' || read.status === 'offline' ? (
          <div className="text-sm text-muted-foreground">
            Couldn&apos;t load recent donations.{' '}
            <button
              type="button"
              onClick={() => void refreshRead(donationsFeedRead, undefined)}
              className="font-medium text-primary underline underline-offset-2"
              data-testid="recent-donations-retry"
            >
              Retry
            </button>
          </div>
        ) : items.length === 0 ? (
          <p className="text-sm text-muted-foreground">No donations to show yet.</p>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((item) => (
              <DonationRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function DonationRow({ item }: { item: DonationFeedItemDto }) {
  return (
    <li
      className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2"
      data-testid="donation-row"
    >
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-foreground">
          {item.donorName}
        </p>
        <p className="text-xs text-muted-foreground">{fmtDateLong(item.date)}</p>
      </div>
      <div className="flex items-center gap-3">
        <DonationStatusBadge status={item.status} />
        <span className="text-sm font-medium text-foreground">
          {formatCents(item.amountCents)}
        </span>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Health / verification banner — renders only for a real problem. Healthy and
// benign states stay silent. Driven by the DB status mirror (no live Stripe call).
// ---------------------------------------------------------------------------

function HealthBanner({
  health,
  loading,
  onFinishVerification,
}: {
  health: DonationHealthDto | undefined;
  loading: boolean;
  onFinishVerification: () => void;
}) {
  if (loading || !health) {
    return (
      <div
        className="h-9 rounded-md bg-muted/40 animate-pulse"
        data-testid="health-banner-skeleton"
      />
    );
  }

  // panelState is only null before an account exists; the dashboard renders only
  // post-onboarding, so fall back defensively to a loud state.
  const state: StripeConnectPanelState = health.panelState ?? 'ActionNeeded';

  // Loud ONLY on a real account problem (§4.1): payouts disabled, past due /
  // restricted, or a live Stripe deadline (Stripe sets `currentDeadline` only when
  // requirements must be met by a date). A benign "requirements due but not overdue"
  // account with payouts still flowing and no deadline is a nudge, not the
  // deadline-critical case, so this dashboard does not render a banner.
  const isProblem =
    !health.payoutsEnabled ||
    state === 'PastDue' ||
    state === 'Restricted' ||
    health.currentDeadline != null;

  if (!isProblem) {
    return null;
  }

  const copy = stripeConnectPanelCopy(state);

  // Payouts-off is the severe case: loud RED (matching the `critical` tone's
  // classes) with a `Ban` prohibition icon. A payouts-on-but-deadline-approaching
  // account is a real problem but not a stoppage — it stays amber with a warning
  // triangle. Severity is carried by icon + bold weight too, never color alone.
  // The tone value in shared is intentionally left as-is so the onboarding
  // StatusPanel (the other consumer of this copy) is not escalated.
  const payoutsOff = arePayoutsPaused(health);
  const loud = payoutsOff
    ? 'border-red-300 bg-red-50 text-red-800'
    : 'border-amber-300 bg-amber-50 text-amber-900';
  const Icon = payoutsOff ? Ban : AlertTriangle;

  return (
    <div
      className={`flex flex-wrap items-center justify-between gap-3 rounded-md border px-3 py-2 ${loud}`}
      data-testid="health-banner-loud"
    >
      <div className="flex items-start gap-2">
        <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
        <div>
          <p className="text-sm font-semibold">{copy.title}</p>
          <p className="text-sm opacity-90">{copy.description}</p>
        </div>
      </div>
      <Button
        size="sm"
        variant="outline"
        onClick={onFinishVerification}
        data-testid="finish-verification"
      >
        Finish verification
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hero card — one metric, with independent loading / error-retry states.
// ---------------------------------------------------------------------------

function HeroCard({
  title,
  subtitle,
  loading,
  error,
  onRetry,
  children,
  testId,
  skeletonTestId,
  retryTestId,
  paused,
  pausedTestId,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  loading: boolean;
  error: boolean;
  onRetry: () => void;
  children: React.ReactNode;
  testId?: string;
  skeletonTestId?: string;
  retryTestId?: string;
  /** Payouts are stopped — show the pause line, not this card's live figure or a
      generic error/retry (which would read as an unrelated glitch). */
  paused?: boolean;
  pausedTestId?: string;
}) {
  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          {title}
        </CardTitle>
        {subtitle && <CardDescription>{subtitle}</CardDescription>}
      </CardHeader>
      <CardContent>
        {paused ? (
          <p className="text-sm text-muted-foreground" data-testid={pausedTestId}>
            Payouts paused — finish verification to resume
          </p>
        ) : loading ? (
          <div
            className="h-8 w-24 rounded bg-muted/40 animate-pulse"
            data-testid={skeletonTestId}
          />
        ) : error ? (
          <div className="text-sm text-muted-foreground">
            Couldn&apos;t load this.{' '}
            <button
              type="button"
              onClick={onRetry}
              className="font-medium text-primary underline underline-offset-2"
              data-testid={retryTestId}
            >
              Retry
            </button>
          </div>
        ) : (
          children
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Fee explainer — a white-label, keyboard- and screen-reader-reachable "what is
// this fee?" affordance on the "Fees this period" card. The accessible name lives
// on the trigger's `aria-label` (always in the DOM), so it works without the
// visual hover/focus tooltip firing — no hover-only reveal for large-text users.
// Never names the processor ("our payments partner", not "Stripe").
// ---------------------------------------------------------------------------

const FEE_EXPLAINER = 'Processing fees deducted from donations.';

function FeeInfoTooltip() {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={FEE_EXPLAINER}
            data-testid="fees-info"
            className="inline-flex items-center justify-center rounded-full text-muted-foreground/70 transition-colors hover:text-muted-foreground focus:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          >
            <Info className="h-3.5 w-3.5" aria-hidden />
          </button>
        </TooltipTrigger>
        {/* Neutral (not the brand-primary default) so the affordance stays monochrome across subsidiaries. */}
        <TooltipContent
          side="top"
          className="max-w-56 border bg-popover text-popover-foreground"
        >
          {FEE_EXPLAINER}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// ---------------------------------------------------------------------------
// Live Stripe money-hero cards — the only two panels that read Stripe live (via
// the connected-account header). Each has its own hook + skeleton/retry, so a
// Stripe hiccup degrades one card to an inline retry, never the whole page or the
// (DB-sourced) health banner. Nothing here is persisted (ADR-011).
// ---------------------------------------------------------------------------

function AvailableToPayOut({ payoutsOff }: { payoutsOff: boolean }) {
  const { data, isLoading, isError, refetch } = useStripeBalance();

  return (
    <HeroCard
      title="Available to pay out"
      loading={isLoading}
      error={isError}
      onRetry={() => void refetch()}
      testId="available-to-payout"
      skeletonTestId="available-skeleton"
      retryTestId="available-retry"
      paused={payoutsOff}
      pausedTestId="available-paused"
    >
      {data && (
        <>
          <p className="text-2xl font-bold text-foreground" data-testid="available-amount">
            {formatCents(data.availableCents)}
          </p>
          {data.availableCents === 0 ? (
            // A "true $0" (this branch is only reached when the balance loaded — a
            // failed load routes to HeroCard's error/retry branch instead). Explain
            // it so it reads as settled-nothing-yet, not a silent failure.
            <>
              <p className="text-sm text-muted-foreground" data-testid="available-zero-caption">
                Nothing ready to pay out yet — donations appear here once they settle
                (usually a few days).
              </p>
              {data.pendingCents > 0 && (
                <p className="text-sm text-muted-foreground" data-testid="available-on-the-way">
                  {formatCents(data.pendingCents)} on the way.
                </p>
              )}
            </>
          ) : (
            data.pendingCents > 0 && (
              <p className="text-sm text-muted-foreground">
                {formatCents(data.pendingCents)} pending
              </p>
            )
          )}
        </>
      )}
    </HeroCard>
  );
}

function NextPayoutCard({ payoutsOff }: { payoutsOff: boolean }) {
  const payoutsQuery = useStripePayouts();
  const scheduleQuery = useStripePayoutSchedule();
  const payouts = payoutsQuery.data?.pages.flatMap((page) => page.payouts) ?? [];
  const next = deriveNextPayout(payouts);
  const needsSchedule = payoutsQuery.data != null && next == null;

  return (
    <HeroCard
      title="Next payout"
      loading={payoutsQuery.isLoading || (needsSchedule && scheduleQuery.isLoading)}
      error={payoutsQuery.isError}
      onRetry={() => void payoutsQuery.refetch()}
      testId="next-payout"
      skeletonTestId="next-payout-skeleton"
      retryTestId="next-payout-retry"
      paused={payoutsOff}
      pausedTestId="next-payout-paused"
    >
      {payoutsQuery.data &&
        (next ? (
          <>
            <p className="text-2xl font-bold text-foreground" data-testid="next-payout-amount">
              {formatCents(next.amountCents)}
            </p>
            <p className="text-sm text-muted-foreground">
              arriving {fmtDateLong(next.arrivalDate)}
            </p>
          </>
        ) : scheduleQuery.isError || !scheduleQuery.data ? (
          <ScheduleError
            onRetry={() => void scheduleQuery.refetch()}
            testId="payout-schedule-retry"
          />
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="next-payout-static">
            {payoutScheduleCopy(scheduleQuery.data)}
          </p>
        ))}
    </HeroCard>
  );
}

function ScheduleError({ onRetry, testId }: { onRetry: () => void; testId: string }) {
  return (
    <p className="text-sm text-muted-foreground">
      Couldn&apos;t load the payout schedule.{' '}
      <button
        type="button"
        onClick={onRetry}
        className="font-medium text-primary underline underline-offset-2"
        data-testid={testId}
      >
        Retry
      </button>
    </p>
  );
}

// ---------------------------------------------------------------------------
// Payouts panel — live, read-only (payouts are platform-controlled; campaigns
// can't trigger or reschedule them). Landing shows the first page (~20); "view all"
// pages full history via Stripe's starting_after cursor.
// ---------------------------------------------------------------------------

function PayoutsPanel() {
  const {
    data,
    isLoading,
    isError,
    refetch,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  } = useStripePayouts();
  const scheduleQuery = useStripePayoutSchedule();
  const payouts = data?.pages.flatMap((page) => page.payouts) ?? [];
  const emptyScheduleLoading =
    data != null && payouts.length === 0 && scheduleQuery.isLoading;

  return (
    <Card data-testid="payouts-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Payouts
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading || emptyScheduleLoading ? (
          <div className="space-y-2" data-testid="payouts-skeleton">
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
            <div className="h-6 rounded bg-muted/40 animate-pulse" />
          </div>
        ) : isError ? (
          <div className="text-sm text-muted-foreground">
            Couldn&apos;t load payouts.{' '}
            <button
              type="button"
              onClick={() => void refetch()}
              className="font-medium text-primary underline underline-offset-2"
              data-testid="payouts-retry"
            >
              Retry
            </button>
          </div>
        ) : payouts.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            {scheduleQuery.isError || !scheduleQuery.data ? (
              <>
                No payouts yet.{' '}
                <ScheduleError
                  onRetry={() => void scheduleQuery.refetch()}
                  testId="payouts-schedule-retry"
                />
              </>
            ) : (
              <p>No payouts yet. {payoutScheduleCopy(scheduleQuery.data)}</p>
            )}
          </div>
        ) : (
          <>
            <ul className="divide-y divide-border">
              {payouts.map((payout) => (
                <PayoutRow key={payout.id} payout={payout} />
              ))}
            </ul>
            {hasNextPage && (
              <div className="pt-3">
                <button
                  type="button"
                  onClick={() => void fetchNextPage()}
                  disabled={isFetchingNextPage}
                  className="text-sm font-medium text-primary underline underline-offset-2 disabled:opacity-60"
                  data-testid="payouts-view-all"
                >
                  {isFetchingNextPage ? 'Loading…' : 'View all'}
                </button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PayoutRow({ payout }: { payout: StripePayoutDto }) {
  return (
    <li
      className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 py-2"
      data-testid="payout-row"
    >
      <div className="flex items-center gap-3">
        <PayoutStatusBadge status={payout.status} />
        <span className="text-xs text-muted-foreground">
          {fmtDateLong(payout.arrivalDate)}
        </span>
      </div>
      <span className="text-sm font-medium text-foreground">
        {formatCents(payout.amountCents)}
      </span>
    </li>
  );
}

// Translate the Stripe payout status to a treasurer-facing pill. "On the way to the
// bank" states (pending/in_transit) are quiet outlines; a failed/canceled payout is
// loud. Falls back to the raw status if Stripe ever adds a value we don't map.
const PAYOUT_STATUS: Record<
  StripePayoutStatus,
  { label: string; variant: 'secondary' | 'outline' | 'destructive' }
> = {
  paid: { label: 'Paid', variant: 'secondary' },
  pending: { label: 'Pending', variant: 'outline' },
  in_transit: { label: 'In transit', variant: 'outline' },
  canceled: { label: 'Canceled', variant: 'destructive' },
  failed: { label: 'Failed', variant: 'destructive' },
};

function PayoutStatusBadge({ status }: { status: string }) {
  const entry = isStripePayoutStatus(status) ? PAYOUT_STATUS[status] : { label: status, variant: 'outline' as const };
  return (
    <Badge variant={entry.variant} data-testid="payout-status">
      {entry.label}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Empty state — shown when the campaign has never received a donation.
// ---------------------------------------------------------------------------

function EmptyDonations({ status }: { status: PaymentsStatusDto }) {
  return (
    <Card data-testid="donations-empty-state">
      <CardContent className="py-8 text-center space-y-2">
        <h3 className="text-base font-semibold text-foreground">No donations yet</h3>
        <p className="text-sm text-muted-foreground">
          Share your donation page so supporters can give.
        </p>
        {status.published && status.donationPagePath ? (
          <p className="pt-1 font-mono text-sm text-foreground" data-testid="empty-donation-link">
            {status.donationPagePath}
          </p>
        ) : (
          <p className="pt-1 text-sm text-muted-foreground">
            Publish your donation page below to get a link to share.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
